import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Modal, InjectedModalProps } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import tokens from 'config/constants/tokens'
import useToast from 'hooks/useToast'
import useVvsBoostHandlers from 'views/xVvs/hooks/useVvsBoostHandlers'
import xVvsContext from 'views/xVvs/context'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useVaultsApr } from 'views/xVvs/hooks/useVaultsApr'
import { formatNumber } from 'utils/formatBalance'
import ConfirmStep from './ConfirmStep'
import InputStep from './InputStep'
import SelectPeriodStep from './SelectPeriodStep'
import { formatLockPeriod, getEndDate } from '../../helpers'
import DepositDetail from './DepositDetail'

const LockModal: React.FC<InjectedModalProps & { customOnDismiss: () => void }> = ({ onDismiss, customOnDismiss }) => {
  const { t } = useTranslation()
  const [pendingTx, setPendingTx] = useState<boolean>()

  const { selectLockPeriod, goNext, goPrev, state } = useContext(xVvsContext)
  const { value, step, selectedPool } = state

  const handleDismiss = useCallback(() => {
    if (customOnDismiss) {
      customOnDismiss()
    }
    onDismiss()
  }, [customOnDismiss, onDismiss])

  const onPeriodSelect = useCallback(
    (pid: number) => {
      selectLockPeriod(pid)
      goNext()
    },
    [goNext, selectLockPeriod],
  )

  const { onDeposit } = useVvsBoostHandlers()
  const vaultsApr = useVaultsApr()
  const poolApr = formatNumber(parseFloat(vaultsApr.find((pool) => pool.poolId === selectedPool?.pid)?.apr || '0'))

  const { toastError } = useToast()

  const title = useMemo(() => {
    if (step === 0) {
      return t('Select Locking Period')
    }
    if (step === 1) {
      return `${formatLockPeriod(selectedPool?.lockPeriod)} ${t('Lock Vault')}`
    }
    return t('Confirm Deposit')
  }, [selectedPool, step, t])

  const addTransaction = useTransactionAdder()
  const handleConfirmClick = async () => {
    setPendingTx(true)
    try {
      const tx = await onDeposit(selectedPool.pid.toString(), value)
      addTransaction(tx, {
        title: t('xVVS Deposit in Vault Completed'),
        summary: t('%value% %symbol% have been locked', {
          value: formatNumber(Number(value), 2, 6),
          symbol: tokens.xvvs.symbol,
        }),
      })
      setPendingTx(false)
      handleDismiss()
    } catch (e: any) {
      console.error(e)
      toastError(t('xVVS Deposit in Vault Failed'), t('Your xVVS amount has been reverted'))
      setPendingTx(false)
    }
    handleDismiss()
  }

  return (
    <Modal title={title} bodyPadding="0" onDismiss={handleDismiss} onBack={step > 0 ? goPrev : undefined}>
      {step === 0 && <SelectPeriodStep onSelect={onPeriodSelect} />}
      {step === 1 && (
        <InputStep
          remark={
            <DepositDetail
              apr={poolApr}
              multiplier={selectedPool?.multiplier}
              endDate={getEndDate(selectedPool?.lockPeriod)}
            />
          }
        />
      )}
      {step === 2 && (
        <ConfirmStep
          onConfirm={handleConfirmClick}
          pendingTx={pendingTx}
          remark={
            <DepositDetail
              apr={poolApr}
              multiplier={selectedPool?.multiplier}
              endDate={getEndDate(selectedPool?.lockPeriod)}
            />
          }
        />
      )}
    </Modal>
  )
}

export default LockModal
